import React, { useEffect, useState } from 'react';
import { Trash2, CreditCard, Truck, Loader2 } from 'lucide-react';
import { BACKEND_URL, fmt, imgUrl, localQuote } from './promoCart';
import { QtySelector, QuestionLink } from './PromoOfferCard';
import { getVisit } from '../../lib/promoAttribution';
import { trackMeta, newEventId, metaContext } from '../../lib/metaPixel';

const FIELDS = [
  ['first_name', 'Prénom *', 'col-span-1'], ['last_name', 'Nom *', 'col-span-1'],
  ['email', 'E-mail *', 'col-span-2'], ['phone', 'Téléphone', 'col-span-1'], ['company', 'Entreprise', 'col-span-1'],
  ['address', 'Adresse de livraison *', 'col-span-2'], ['postal_code', 'NPA *', 'col-span-1'], ['city', 'Localité *', 'col-span-1'],
];
const REQUIRED = ['first_name', 'last_name', 'email', 'address', 'postal_code', 'city'];
const METHODS = [['card', 'Payer par carte'], ['qr_invoice', 'Payer par facture QR']];

const errorText = (d) => (typeof d?.detail === 'string' ? d.detail : Array.isArray(d?.detail) ? 'Vérifiez les champs (e-mail valide, champs obligatoires).' : 'Erreur, veuillez réessayer.');

const Totals = ({ quote }) => (
  <div className="space-y-1 text-sm" data-testid="promo-cart-totals">
    <div className="flex justify-between text-zinc-400"><span>Sous-total HT</span><span className="tabular-nums" data-testid="promo-cart-subtotal">CHF {fmt(quote.subtotal_ht)}</span></div>
    <div className="flex justify-between text-zinc-400"><span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Livraison</span>
      <span className="tabular-nums" data-testid="promo-cart-shipping">{quote.shipping_ttc ? `CHF ${fmt(quote.shipping_ttc)} TTC` : 'offerte (CHF 0.00)'}</span></div>
    <div className="flex justify-between text-zinc-400"><span>TVA {quote.tva_rate} %</span><span className="tabular-nums">CHF {fmt(quote.tva_amount)}</span></div>
    <div className="flex justify-between text-white text-lg font-bold pt-2 border-t border-zinc-800"><span>Total TTC</span><span className="tabular-nums text-amber-400" data-testid="promo-cart-total">CHF {fmt(quote.total_ttc)}</span></div>
  </div>
);

const Line = ({ l, o, q, cart }) => (
  <div className="flex gap-3" data-testid={`promo-cart-line-${l.slug}`}>
    <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center shrink-0 overflow-hidden">{o.images?.[0] && <img src={imgUrl(o.images[0])} alt="" className="max-h-full max-w-full object-contain" />}</div>
    <div className="flex-1 min-w-0 space-y-2">
      <p className="text-sm text-white font-medium leading-snug">{o.name}</p>
      {(o.sold_out || l.quantity > (o.stock || 0)) && <p className="text-xs text-red-400" data-testid={`promo-cart-stock-warning-${l.slug}`}>{o.sold_out ? 'Épuisé : retirez cet article' : `Seulement ${o.stock} pièce(s) disponible(s)`}</p>}
      <div className="flex items-center gap-3">
        <QtySelector value={l.quantity} onChange={(v) => cart.setQty(l.slug, Math.min(v, o.stock || v))} max={o.stock} testid={`promo-cart-qty-${l.slug}`} />
        <button onClick={() => cart.setQty(l.slug, 0)} className="text-zinc-500 hover:text-red-400" data-testid={`promo-cart-remove-${l.slug}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {q && <p className="text-xs text-zinc-400 tabular-nums" data-testid={`promo-cart-unit-${l.slug}`}>CHF {fmt(q.unit_price)} HT / pce ({fmt(q.unit_price_ttc)} TTC) · <span className="text-white">CHF {fmt(q.line_total_ht)} HT</span></p>}
    </div>
  </div>
);

// Parcours de paiement unique des offres promo (panier latéral Promotions + page Panier)
export const PromoCheckout = ({ cart, offers, active = true, footerClass = '' }) => {
  const [serverQuote, setServerQuote] = useState(null);
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map(([k]) => [k, ''])));
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [method, setMethod] = useState('card');
  const [invoiceResult, setInvoiceResult] = useState(null);
  const bySlug = Object.fromEntries(offers.map((o) => [o.slug, o]));
  const lines = cart.lines.filter((l) => bySlug[l.slug]);
  const linesKey = JSON.stringify(lines);
  const upToDate = serverQuote?.key === linesKey;
  const quote = lines.length ? (upToDate ? serverQuote.data : localQuote(lines, bySlug)) : null;

  useEffect(() => {
    if (!active || lines.length === 0) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      fetch(`${BACKEND_URL}/api/promo-offers/cart/quote`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: lines }),
      }).then((r) => (r.ok ? r.json() : null)).then((d) => { if (!cancelled && d) setServerQuote({ key: linesKey, data: d }); }).catch(() => {});
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [active, linesKey]);

  const pay = async () => {
    if (!upToDate) return;
    if (REQUIRED.some((k) => !form[k].trim())) { setError('Merci de remplir tous les champs obligatoires (*).'); return; }
    setError(''); setPaying(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/promo-offers/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: lines, customer: form, origin_url: window.location.origin, attribution: getVisit()?.attribution, visit_id: getVisit()?.visit_id, meta: metaContext(newEventId()), payment_method: method }),
      });
      const d = await res.json();
      if (res.ok && (d.checkout_url || d.payment_method === 'qr_invoice')) {
        trackMeta('InitiateCheckout', { value: quote.total_ht, currency: 'CHF', num_items: lines.reduce((n, l) => n + l.quantity, 0),
          content_ids: lines.map((l) => l.slug), content_type: 'product' });
        if (d.payment_method === 'qr_invoice') { setInvoiceResult(d); cart.clear(); setPaying(false); return; }
        window.location.href = d.checkout_url; return;
      }
      setError(errorText(d));
    } catch (e) { setError('Connexion impossible, veuillez réessayer.'); }
    setPaying(false);
  };

  return (
    <>
      <div className="space-y-5">
        {invoiceResult && (
          <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-4 space-y-2" data-testid="promo-invoice-success">
            <p className="text-white font-semibold">Commande {invoiceResult.order_number} enregistrée</p>
            <p className="text-sm text-zinc-300">La facture QR {invoiceResult.invoice_number} (CHF {fmt(invoiceResult.total_ttc)}) vous a été envoyée par e-mail. Délai de paiement : {invoiceResult.terms_days} jours.</p>
            <p className="text-sm text-amber-300">Livraison après réception du paiement.</p>
            <QuestionLink subject={`Question sur la commande ${invoiceResult.order_number}`} testid="promo-invoice-question" />
          </div>
        )}
        {lines.length === 0 && !invoiceResult && <p className="text-zinc-500 text-center py-10" data-testid="promo-cart-empty">Votre panier est vide.</p>}
        {lines.map((l) => <Line key={l.slug} l={l} o={bySlug[l.slug]} q={quote?.items?.find((i) => i.offer_slug === l.slug)} cart={cart} />)}
        {quote && <Totals quote={quote} />}
        {lines.length > 0 && (
          <div className="space-y-3 pt-2">
            <p className="text-sm font-semibold text-white">Vos coordonnées (sans création de compte)</p>
            <div className="grid grid-cols-2 gap-2">
              {FIELDS.map(([k, label, span]) => (
                <input key={k} placeholder={label} type={k === 'email' ? 'email' : 'text'} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className={`${span} h-11 px-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:border-amber-500 outline-none`} data-testid={`promo-checkout-${k}`} />
              ))}
            </div>
          </div>
        )}
      </div>
      {lines.length > 0 && (
        <div className={`space-y-2 ${footerClass}`}>
          {error && <p className="text-sm text-red-400" data-testid="promo-checkout-error">{error}</p>}
          <div className="grid grid-cols-2 gap-2" data-testid="promo-payment-method">
            {METHODS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`promo-method-${k}`}
                className={`h-11 rounded-lg text-sm border transition-colors ${method === k ? 'border-amber-500 bg-amber-500/10 text-amber-300' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>{l}</button>
            ))}
          </div>
          {method === 'qr_invoice' && <p className="text-xs text-zinc-400" data-testid="promo-qr-info">Facture QR envoyée par e-mail, paiement d'avance. Livraison après réception du paiement.</p>}
          <button onClick={pay} disabled={paying || !upToDate} className="w-full h-12 bg-amber-500 text-black rounded-lg font-bold hover:bg-amber-400 disabled:opacity-40 flex items-center justify-center gap-2" data-testid="promo-checkout-pay">
            {paying || !upToDate ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} {!upToDate ? 'Calcul du prix…' : method === 'card' ? `Payer par carte CHF ${fmt(quote.total_ttc)}` : `Recevoir la facture QR CHF ${fmt(quote.total_ttc)}`}
          </button>
          <button onClick={() => { if (window.confirm('Vider le panier des offres promo ?')) cart.clear(); }} className="w-full h-11 text-sm text-zinc-400 hover:text-red-400 flex items-center justify-center gap-1.5" data-testid="promo-cart-clear">
            <Trash2 className="w-4 h-4" /> Vider le panier
          </button>
          <p className="text-center text-zinc-600 text-xs">Paiement sécurisé par carte (Stripe) · Visa, Mastercard</p>
        </div>
      )}
    </>
  );
};
